import React, { useEffect, useState } from "react";
import { apiPost, apiPut } from "../../API/Api_Url";
import { toast } from "react-toastify";
import { FaArrowLeft } from "react-icons/fa";

function SupplierAddQuotationModel({
  RequestData,
  isOpen,
  onClose,
  GetAllPendingMaterialRequest,
  setExpandedJob
}) {
  const [MaterialRequest, setMaterialRequest] = useState("");
  const [QuotedItems, setQuotedItems] = useState([]);
  const [GST, setGST] = useState("");
  const [DeliveryTime, setDeliveryTime] = useState("");
  const [CreditTerms, setCreditTerms] = useState("");
  const [Note, setNote] = useState("");
  const [IsLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (RequestData) {
      setMaterialRequest(RequestData?._id || "");
      const initialItems =
        RequestData?.MaterialItems?.map((item) => ({
          materialName: item.materialName || "",
          quantity: item.quantity || 0,
          unit: item.unit || "",
          pricePerUnit: "",
          totalPrice: "",
          brand: item.brandPreference || "",
          specifications: item.specifications || "",
        })) || [];
      setQuotedItems(initialItems);
    }
  }, [RequestData]);

  const handleItemChange = (index, field, value) => {
    setQuotedItems((prev) =>
      prev.map((item, i) => {
        if (i === index) {
          const updated = { ...item, [field]: value };
          if (field === "pricePerUnit" || field === "quantity") {
            const qty = Number(updated.quantity) || 0;
            const price = Number(updated.pricePerUnit) || 0;
            updated.totalPrice = qty * price;
          }
          return updated;
        }
        return item;
      })
    );
  };

  const handleSubmit = async () => {
    let newErrors = {};
    if (!QuotedItems.length) newErrors.QuotedItems = "At least one item required";
    if (!GST) newErrors.GST = "GST is required";
    if (!DeliveryTime) newErrors.DeliveryTime = "Delivery time is required";
    if (!CreditTerms) newErrors.CreditTerms = "Credit terms are required";
    if (!Note) newErrors.Note = "Note is required";

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    try {
      setIsLoading(true);
      const payload = {
        MaterialRequest,
        QuotedItems,
        GST: Number(GST),
        DeliveryTime,
        CreditTerms,
        Note,
      };

      const response = await apiPost(
        `api/material-supplier/CreateMaterialRequestBid`,
        payload
      );

      if (response?.data) {
        toast.success(response?.message || "Quotation created successfully!");
        onClose();
        setExpandedJob(null)
        GetAllPendingMaterialRequest();
      } else {
        toast.error(response?.message || "Failed to create quotation");
      }
    } catch (error) {
      console.error("Error creating quotation:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-lg p-6 max-w-5xl w-full overflow-y-auto max-h-[95vh]">
        <div className="flex items-center mb-6 border-b pb-3">
          <FaArrowLeft
            className="mr-3 cursor-pointer text-gray-600 hover:text-gray-800 transition"
            onClick={onClose}
          />
          <h2 className="text-2xl font-bold text-gray-800">
            Supplier Add Quotation
          </h2>
        </div>
        <div className="space-y-6">
          {QuotedItems?.map((item, idx) => (
            <div
              key={idx}
              className="border rounded-xl p-6 shadow-sm bg-gray-50 hover:shadow-md transition"
            >
              <h3 className="font-semibold text-lg text-gray-800 mb-4 border-b pb-2">
                {item?.materialName}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    Quantity
                  </label>
                  <input
                    type="number"
                    value={item?.quantity}
                    disabled
                    className="w-full border rounded-lg px-3 py-2 bg-gray-100"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Unit</label>
                  <input
                    type="text"
                    value={item?.unit}
                    disabled
                    className="w-full border rounded-lg px-3 py-2 bg-gray-100"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    Price per Unit
                  </label>
                  <input
                    type="number"
                    value={item?.pricePerUnit}
                    onChange={(e) =>
                      handleItemChange(idx, "pricePerUnit", e.target.value)
                    }
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    Total Price
                  </label>
                  <input
                    type="number"
                    value={item?.totalPrice}
                    disabled
                    className="w-full border rounded-lg px-3 py-2 bg-gray-100"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Brand</label>
                  <input
                    type="text"
                    value={item?.brand}
                    onChange={(e) =>
                      handleItemChange(idx, "brand", e.target.value)
                    }
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div className="col-span-2 md:col-span-3">
                  <label className="text-sm font-medium text-gray-600">
                    Specifications
                  </label>
                  <input
                    type="text"
                    value={item?.specifications}
                    onChange={(e) =>
                      handleItemChange(idx, "specifications", e.target.value)
                    }
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
              </div>
            </div>
          ))}
          {errors.QuotedItems && (
            <p className="text-red-500 text-sm">{errors.QuotedItems}</p>
          )}
        </div>

        <div className="mt-8 bg-gray-50 p-6 rounded-xl shadow-inner space-y-6">
          <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">
            Quotation Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium text-gray-600">GST (%)</label>
              <input
                type="number"
                value={GST}
                onChange={(e) => setGST(e.target.value)}
                className="w-full border rounded-lg px-3 py-2"
              />
              {errors.GST && (
                <p className="text-red-500 text-sm mt-1">{errors.GST}</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">
                Delivery Time
              </label>
              <input
                type="text"
                value={DeliveryTime}
                onChange={(e) => setDeliveryTime(e.target.value)}
                className="w-full border rounded-lg px-3 py-2"
              />
              {errors.DeliveryTime && (
                <p className="text-red-500 text-sm mt-1">{errors.DeliveryTime}</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">
                Credit Terms
              </label>
              <input
                type="text"
                value={CreditTerms}
                onChange={(e) => setCreditTerms(e.target.value)}
                className="w-full border rounded-lg px-3 py-2"
              />
              {errors.CreditTerms && (
                <p className="text-red-500 text-sm mt-1">{errors.CreditTerms}</p>
              )}
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-gray-600">Note</label>
              <textarea
                value={Note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                className="w-full border rounded-lg px-3 py-2"
              />
              {errors.Note && (
                <p className="text-red-500 text-sm mt-1">{errors.Note}</p>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 flex justify-end gap-4 border-t pt-4">
          <button
            onClick={onClose}
            className="px-6 py-2 border rounded-lg hover:bg-gray-100 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={IsLoading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
          >
            {IsLoading ? "Saving..." : "Submit Quotation"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default SupplierAddQuotationModel;
